// Documentation ----> https://popper.js.org/
import React, { useCallback, useEffect, useRef, useState } from 'react';
import reactDom from 'react-dom';
import { usePopper } from 'react-popper';
import './Popup.scss';
import { useOutsideAlerter } from '../Utils/Utils';

const Popup = (props) => {
	const { delay, disabled, className, offsetY, offsetX, triggerElement, popupElement, popupElementClass, hoverOnly, onClick, 
		absolute, popupOuterWidth, placement, display, children, zIndex, relative, outerStyle, clickAway, setPopupMenuWith, 
		hoverAway, stopPropagation } = props;

	const [referenceElement, setReferenceElement] = useState(null);
	const [popperElement, setPopperElement] = useState(null);
	const popperElementRef = useRef(null);
	const setPopperElementAndRef = useCallback((el) => {
		popperElementRef.current = el;
		setPopperElement(el);
	}, []);
	const [mouseLeaveTimer, setMouseLeaveTimer] = useState(null)
	const [mouseEnterTimer, setMouseEnterTimer] = useState(null)
	const [popupVisible, setPopupVisible] = useState(false);
	const wrapperElementRef = useRef()

	useOutsideAlerter({current: popperElement}, () => { !hoverOnly && setPopupVisible(false); });

	const { styles, attributes } = usePopper(referenceElement, popperElement, {
		placement: placement ? placement : "bottom-start",
		strategy: absolute ? "" : "fixed",
		modifiers: [{ name: "offset", options: { offset: [offsetX ? offsetX : 0, offsetY ? offsetY : 4] } }],
	});

	useEffect(() => {
		if (clickAway) {
			function handleClickOutside(event) {
				if (wrapperElementRef.current && !wrapperElementRef.current.contains(event.target)
					&& !(popperElementRef.current && popperElementRef.current.contains(event.target))) {
					setMouseLeaveTimer(setTimeout(() => {
						setPopupVisible(false)
					}, 200))
				}
			}
			// Bind the event listener
			document.addEventListener("mousedown", handleClickOutside);
			return () => {
				// Unbind the event listener on clean up
				document.removeEventListener("mousedown", handleClickOutside);
			};
		}
		if (hoverAway) {
			function handleHoverOutside(event) {
				if (event && event.target && event.target.className && typeof event.target.className === 'string' && event.target.className.includes(hoverAway)) {
					setMouseLeaveTimer(setTimeout(() => {
						setPopupVisible(false)
					}, 200))
				}
			}
			document.addEventListener("mouseover", handleHoverOutside);
			return () => {
				document.removeEventListener("mouseover", handleHoverOutside);
			};
		}
	}, [wrapperElementRef]);

	useEffect(() => {
		if (setPopupMenuWith && wrapperElementRef.current) {
			setPopupMenuWith(wrapperElementRef.current.clientWidth)
		}
	}, [wrapperElementRef.current, popupVisible])

	const handleMouseEnter = () => {
		if (disabled) {
			return;
		} else if (hoverOnly) {
			clearTimeout(mouseLeaveTimer)
			if (delay) {
				setMouseEnterTimer(setTimeout(() => {
					setPopupVisible(true)
				}, delay))
			} else {
				setPopupVisible(true)
			}
		}
	}

	const handleMouseLeave = () => {
		clearTimeout(mouseEnterTimer)
		if (disabled) {
			return;
		} else if (hoverOnly) {
			setMouseLeaveTimer(setTimeout(() => {
				setPopupVisible(false)
			}, 100))
		}
	}

	const popUpChild = (
		<div
			className={`popup-element-outer${popupElementClass ? " " + popupElementClass : ""}`}
			onClick={(e) => {
				if (stopPropagation) e.stopPropagation()
				setPopupVisible(!popupVisible)
			}}
			ref={setPopperElementAndRef}
			style={{ ...styles.popper, width: popupOuterWidth, zIndex: zIndex }}
			{...attributes.popper}
		>
			{React.cloneElement(popupElement, { setPopupVisible })}
		</div>
	);


	return (
		<div
			style={{ ...outerStyle, display: display ? display : "" }}
			className={`popup-outer fade-down${className ? " " + className : ""}${relative ? " popup-relative" : ""}`}
			onMouseEnter={() => handleMouseEnter()}
			onMouseLeave={() => handleMouseLeave()}
			ref={wrapperElementRef}
		>
			<div
				className='popup-trigger'
				onClick={(e) => {
					if (disabled) {
						if (stopPropagation) e.stopPropagation()
						return null
					} else {
						if (stopPropagation) e.stopPropagation()
						if (hoverOnly && onClick) onClick()
						else if (hoverOnly) return null
						else {
							setPopupVisible(!popupVisible);
							onClick && onClick();
						}
					}
				}}
				ref={setReferenceElement}
			>
				{triggerElement ? triggerElement : children}
			</div>
			{(popupVisible && absolute) ? popUpChild : popupVisible && reactDom.createPortal(popUpChild, document.querySelector("#tooltip-portal"))}
		</div>
	);
};

export default Popup;